import {
  LEGACY_RISK_MATRIX,
  RA_CATEGORIES,
  RA_DEPARTMENTS,
  RISK_LEVELS,
  type RiskLevel,
} from '@felix/domain';
import { ActionIcon, Button, CloseButton, Modal, Select, Tooltip } from '@mantine/core';
import {
  IconDownload,
  IconEdit,
  IconEye,
  IconPlus,
  IconPrinter,
  IconSearch,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { errorCode } from '../personnel/source.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import { ReturnedBanner } from '../returned/Returned.js';
import { LibraryStart } from './master/LibraryStart.js';
import type { RaMasterView } from './master/types.js';
import classes from './Risk.module.css';
import { ControlTable, RiskForm, type RiskTab } from './RiskForm.js';
import {
  ActionDialog,
  DecisionTree,
  LevelBadge,
  RiskKpis,
  RiskMatrix,
  RiskStatusBadge,
  Scenarios,
  ScoringGuide,
  useRiskError,
} from './RiskParts.js';
import type { RiskSource } from './source.js';
import type {
  RiskAssessmentContent,
  RiskDetail,
  RiskLibraryEntry,
  RiskMatrixRef,
  RiskMatrixSettings,
  RiskStatus,
  RiskSummary,
} from './types.js';
import {
  EMPTY_FILTER,
  filterRisks,
  localId,
  type MatrixMode,
  newControl,
  type RiskFilter,
  type RiskSortKey,
  riskCsv,
  sortRisks,
} from './view.js';

const PAGE_SIZE = 8;
const STATUSES: RiskStatus[] = ['draft', 'pending', 'returned', 'approved', 'withdrawn'];

type Dialog = 'submit' | 'approve' | 'return' | 'withdraw' | 'discard' | null;

function contentOf(detail: RiskDetail): RiskAssessmentContent {
  return {
    title: detail.title,
    activity: detail.activity,
    department: detail.department,
    category: detail.category,
    location: detail.location,
    assessmentDay: detail.assessmentDay,
    reviewDay: detail.reviewDay,
    hazards: detail.hazards,
    controls: detail.controls,
  };
}

export interface RiskAssessmentScreenProps {
  source: RiskSource;
  /** Panodan açılan değerlendirme (ADR 0156): ilk açılışta formu açılır. */
  initialId?: string | null;
}

/** Bağlantı sorgusu `?vessel=&id=` (panodaki iş satırı; ADR 0156 "Yollar"). */
export function riskLinkOf(search: string): { vesselId: string | null; id: string | null } {
  const params = new URLSearchParams(search);
  return { vesselId: params.get('vessel'), id: params.get('id') };
}

/**
 * Compliance ▸ Risk Assessment (M13d; ADR 0050; `gemi-risk-degerlendirme.jpeg`): özet kartları,
 * süzgeçler, risk matrisi ve listesi, form, kontrol önlemleri, puanlama rehberi, şablon
 * kütüphanesi ve karar ağacı. Gemide değerlendirme yazılır ve gönderilir; ofiste görülür, ofis
 * adımında onaylanır ve şablonlar yönetilir. Ekran iki uygulamada aynıdır (ADR 0019 §2).
 */
export function RiskAssessmentScreen({ source, initialId = null }: RiskAssessmentScreenProps) {
  const { t } = useTranslation();
  const message = useRiskError();
  const editor = source.editor;
  const office = source.scope === 'web';

  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; items: RiskSummary[] }
  >({ kind: 'loading' });
  const [vessels, setVessels] = useState<{ id: string; name: string }[]>([]);
  // Şirket RA kütüphanesinin yayımlı sürümleri (K3; ADR 0087 §5): gemi RA'sı bunlardan başlar
  const [library, setLibrary] = useState<
    { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; list: RiskLibraryEntry[] }
  >({ kind: 'loading' });
  const [starting, setStarting] = useState(false);
  const [draftFilter, setDraftFilter] = useState<RiskFilter>(EMPTY_FILTER);
  const [filter, setFilter] = useState<RiskFilter>(EMPTY_FILTER);
  const [sort, setSort] = useState<SortState<RiskSortKey>>({ key: 'raNo', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState<MatrixMode>('initial');
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<RiskDetail | null>(null);
  const [draft, setDraft] = useState<RiskAssessmentContent | null>(null);
  const [tab, setTab] = useState<RiskTab>('general');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{
    tone: 'info' | 'warning' | 'danger' | 'success';
    text: string;
  } | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  // Şirketin risk matrisi profilleri (ADR 0085): yoksa ya da okunamazsa Legacy 5-band
  const [matrixSettings, setMatrixSettings] = useState<RiskMatrixSettings | null>(null);
  // Şirket risk kütüphanesi (K2; ADR 0086 §7): gemi RA'sı kütüphaneden başlar (soru 8)
  const [master, setMaster] = useState<RaMasterView | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setState((current) => (current.kind === 'ready' ? current : { kind: 'loading' }));
    try {
      setState({ kind: 'ready', items: await source.list() });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);

  const loadLibrary = useCallback(async () => {
    try {
      setLibrary({ kind: 'ready', list: await source.library() });
    } catch {
      setLibrary({ kind: 'error' });
    }
  }, [source]);

  useEffect(() => {
    void load();
    void loadLibrary();
    source.vessels().then(setVessels, () => setVessels([]));
    source.matrix?.().then(setMatrixSettings, () => setMatrixSettings(null));
    if (source.editor) source.master?.().then(setMaster, () => setMaster(null));
  }, [source, load, loadLibrary]);

  const dirty = useMemo(
    () => (detail && draft ? JSON.stringify(contentOf(detail)) !== JSON.stringify(draft) : false),
    [detail, draft],
  );

  const show = (next: RiskDetail, text: string | null = null) => {
    setDetail(next);
    setDraft(contentOf(next));
    setSelected(next.id);
    setNotice(text ? { tone: 'success', text } : null);
  };

  const open = async (id: string, nextTab: RiskTab = 'general') => {
    if (dirty && !window.confirm(t('common.unsaved.message'))) return;
    setSelected(id);
    setTab(nextTab);
    setNotice(null);
    try {
      show(await source.detail(id));
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      setNotice({ tone: 'danger', text: message(errorCode(error)) });
    }
  };

  // Panodan gelen değerlendirme bir kez açılır (liste okununca; form listenin altında)
  const linked = useRef(initialId);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `open` her çizimde yeni; bir kez çalışır
  useEffect(() => {
    if (state.kind !== 'ready' || !linked.current) return;
    const id = linked.current;
    linked.current = null;
    void open(id);
  }, [state.kind]);

  const items = state.kind === 'ready' ? state.items : [];
  const filtered = useMemo(
    () => sortRisks(filterRisks(items, filter), sort),
    [items, filter, sort],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const k = (key: string, options?: Record<string, unknown>) => t(`risk.${key}`, options ?? {});

  const create = async (versionId: string) => {
    if (!editor) return;
    if (dirty && !window.confirm(t('common.unsaved.message'))) return;
    setStarting(false);
    try {
      const created = await editor.create(versionId);
      show(created);
      setTab('general');
      await load();
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      setNotice({ tone: 'danger', text: message(errorCode(error)) });
    }
  };

  const save = async () => {
    if (!editor || !detail || !draft) return;
    setBusy(true);
    try {
      show(await editor.update(detail.id, detail.rowVersion, draft), t('risk.form.saved'));
      await load();
    } catch (error) {
      setNotice({ tone: 'danger', text: message(errorCode(error)) });
    } finally {
      setBusy(false);
    }
  };

  /** Pencere işlemleri: hata kodu döner (pencere açık kalır) ya da `null`. */
  const act = async (fn: () => Promise<RiskDetail | null>): Promise<string | null> => {
    try {
      const next = await fn();
      if (next) show(next);
      else {
        setDetail(null);
        setDraft(null);
        setSelected(null);
      }
      await load();
      return null;
    } catch (error) {
      return errorCode(error);
    }
  };

  const expected =
    detail?.pending && detail.contentHash
      ? {
          submissionId: detail.pending.submissionId,
          step: detail.pending.step,
          contentHash: detail.contentHash,
        }
      : null;

  const editable = Boolean(editor && detail?.can.edit);
  const formNotice =
    notice ??
    (detail
      ? office && !detail.can.decide
        ? { tone: 'info' as const, text: t('risk.form.readOnlyOffice') }
        : detail.status === 'pending' && detail.pending
          ? {
              tone: 'info' as const,
              text: t(detail.pending.office ? 'risk.form.pendingOffice' : 'risk.form.pendingShip', {
                step: detail.pending.step,
                steps: detail.pending.steps,
              }),
            }
          : detail.status === 'returned'
            ? { tone: 'warning' as const, text: t('risk.form.returnedNote') }
            : detail.changed && detail.status === 'approved'
              ? { tone: 'info' as const, text: t('risk.form.changedNote') }
              : null
      : null);

  const nextRevision = (detail?.revisionNo ?? 0) + 1;
  const formActions = detail ? (
    <>
      {editor && detail.can.submit ? (
        <Button size="sm" variant="light" disabled={dirty} onClick={() => setDialog('submit')}>
          {nextRevision > 1 ? k('form.submitRevision') : k('form.submit')}
        </Button>
      ) : null}
      {detail.can.decide && expected ? (
        <>
          <Button size="sm" color="green" onClick={() => setDialog('approve')}>
            {k('form.approve')}
          </Button>
          <Button size="sm" variant="light" color="warning" onClick={() => setDialog('return')}>
            {k('form.return')}
          </Button>
        </>
      ) : null}
      {editor && detail.can.discard ? (
        <Button size="sm" variant="subtle" color="red" onClick={() => setDialog('discard')}>
          {k('form.discard')}
        </Button>
      ) : null}
      {editor && detail.can.withdraw ? (
        <Button size="sm" variant="subtle" color="red" onClick={() => setDialog('withdraw')}>
          {k('form.withdraw')}
        </Button>
      ) : null}
    </>
  ) : null;

  const exportCsv = () => {
    const csv = riskCsv(filtered, (key) => t(key));
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'risk-assessments.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const options = <T extends string>(list: readonly T[], prefix: string, all: string) => [
    { value: '', label: k(all) },
    ...list.map((v) => ({ value: v, label: t(`risk.${prefix}.${v}`) })),
  ];
  const entries = library.kind === 'ready' ? library.list : [];
  const currentEntries = entries.filter((e) => e.current);
  const matrix = matrixSettings?.current?.definition ?? LEGACY_RISK_MATRIX;
  /** Matris sürümünün adı: profil ve sürüm; boş kimlik Legacy 5-band (ADR 0085 §4). */
  const matrixLabel = (ref: RiskMatrixRef): string => {
    if (!ref.versionId) return k('matrixProfile.legacy');
    const profile = matrixSettings?.profiles.find((p) =>
      p.versions.some((v) => v.id === ref.versionId),
    );
    return k('matrixProfile.versionLabel', {
      name: profile?.name ?? k('matrixProfile.title'),
      version: ref.version ?? '',
    });
  };
  const inUse = matrixSettings?.current ?? null;
  // Seviye süzgeci: geçerli matrisin bantları ve listede görünen öteki seviyeler (eski revizyonlar)
  const levels = RISK_LEVELS.filter(
    (level) =>
      matrix.bands.some((b) => b.key === level) || items.some((i) => i.initial?.level === level),
  );
  // Başladığı yayımlı şirket sürümü (ADR 0087 §5); eski değerlendirmede şablon kodu yok
  const source_ = detail?.sourceVersionId
    ? (entries.find((e) => e.id === detail.sourceVersionId) ?? null)
    : null;
  const templateCode = source_ ? `${source_.code} ${source_.revision}` : null;

  return (
    <div className={classes.page}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        crumb={k('crumbList')}
        icon={<IconShieldCheck size={26} />}
        actions={
          <>
            {editor ? (
              <Button leftSection={<IconPlus size={16} />} onClick={() => setStarting(true)}>
                {k('newAssessment')}
              </Button>
            ) : null}
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              onClick={exportCsv}
              disabled={filtered.length === 0}
            >
              {k('export')}
            </Button>
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              onClick={() => window.print()}
            >
              {k('print')}
            </Button>
          </>
        }
      />
      <RiskKpis items={items} />
      <form
        className={classes.filters}
        data-office={office || undefined}
        aria-label={k('filter.label')}
        onSubmit={(event) => {
          event.preventDefault();
          setFilter({ ...draftFilter, cell: filter.cell });
          setPage(1);
        }}
      >
        <Select
          label={k('filter.vessel')}
          value={office ? (draftFilter.vesselId ?? '') : (vessels[0]?.id ?? '')}
          readOnly={!office}
          allowDeselect={false}
          data={
            office
              ? [
                  { value: '', label: k('filter.allVessels') },
                  ...vessels.map((v) => ({ value: v.id, label: v.name })),
                ]
              : vessels.map((v) => ({ value: v.id, label: v.name }))
          }
          onChange={(v) => setDraftFilter({ ...draftFilter, vesselId: v || null })}
        />
        <Select
          label={k('filter.department')}
          value={draftFilter.department ?? ''}
          allowDeselect={false}
          data={options(RA_DEPARTMENTS, 'departments', 'filter.allDepartments')}
          onChange={(v) =>
            setDraftFilter({ ...draftFilter, department: (v || null) as RiskFilter['department'] })
          }
        />
        <Select
          label={k('filter.category')}
          value={draftFilter.category ?? ''}
          allowDeselect={false}
          data={options(RA_CATEGORIES, 'categories', 'filter.allCategories')}
          onChange={(v) =>
            setDraftFilter({ ...draftFilter, category: (v || null) as RiskFilter['category'] })
          }
        />
        <Select
          label={k('filter.level')}
          value={draftFilter.level ?? ''}
          allowDeselect={false}
          data={options(levels, 'levels', 'filter.allLevels')}
          onChange={(v) =>
            setDraftFilter({ ...draftFilter, level: (v || null) as RiskLevel | null })
          }
        />
        <Select
          label={k('filter.status')}
          value={draftFilter.status ?? ''}
          allowDeselect={false}
          data={options(STATUSES, 'statuses', 'filter.allStatuses')}
          onChange={(v) =>
            setDraftFilter({ ...draftFilter, status: (v || null) as RiskStatus | null })
          }
        />
        <div className={classes.dates}>
          <DayInput
            label={k('filter.from')}
            value={draftFilter.from}
            maxDate={draftFilter.to ?? undefined}
            onChange={(v) => setDraftFilter({ ...draftFilter, from: v })}
          />
          <DayInput
            label={k('filter.to')}
            value={draftFilter.to}
            minDate={draftFilter.from ?? undefined}
            onChange={(v) => setDraftFilter({ ...draftFilter, to: v })}
          />
        </div>
        <Button type="submit" leftSection={<IconSearch size={16} />}>
          {k('filter.search')}
        </Button>
      </form>
      {filter.cell ? (
        <div className={classes.chips}>
          <StatusBadge tone="info" withIcon={false}>
            {k('filter.cell', {
              likelihood: filter.cell.likelihood,
              severity: filter.cell.severity,
            })}
          </StatusBadge>
          <CloseButton
            size="sm"
            aria-label={k('filter.clearCell')}
            onClick={() => setFilter({ ...filter, cell: null })}
          />
        </div>
      ) : null}

      <div className={classes.row2}>
        <RiskMatrix
          matrix={matrix}
          label={matrixLabel({
            versionId: inUse?.versionId ?? null,
            version: inUse?.version ?? null,
          })}
          items={filterRisks(items, { ...filter, cell: null })}
          mode={mode}
          onMode={(next) => {
            setMode(next);
            setFilter({ ...filter, cell: null });
          }}
          selected={filter.cell}
          onCell={(likelihood, severity) => {
            const same =
              filter.cell?.likelihood === likelihood && filter.cell.severity === severity;
            setFilter({ ...filter, cell: same ? null : { likelihood, severity, mode } });
            setPage(1);
          }}
        />
        <Panel title={k('list.title')}>
          {state.kind === 'error' ? (
            <LoadError title={k('list.error')} code={state.code} onRetry={() => void load()} />
          ) : state.kind === 'loading' ? (
            <TableSkeleton columns={8} label={k('list.table')} />
          ) : items.length === 0 ? (
            <EmptyState
              title={k('list.emptyTitle')}
              description={k(editor ? 'list.emptyShip' : 'list.emptyWeb')}
            />
          ) : filtered.length === 0 ? (
            <NoMatch
              title={k('list.noMatch')}
              onClear={() => {
                setDraftFilter(EMPTY_FILTER);
                setFilter(EMPTY_FILTER);
                setPage(1);
              }}
            />
          ) : (
            <>
              <section
                className={classes.tableWrap}
                aria-label={k('list.table')}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th scope="col">{k('list.columns.no')}</th>
                      {(['title', 'vessel', 'department'] as const).map((column) => (
                        <SortableTh
                          key={column}
                          column={column}
                          label={k(`list.columns.${column}`)}
                          sort={sort}
                          onSort={setSort}
                        />
                      ))}
                      <th scope="col">{k('list.columns.level')}</th>
                      <SortableTh
                        column="score"
                        label={k('list.columns.score')}
                        sort={sort}
                        onSort={setSort}
                        numeric
                      />
                      <SortableTh
                        column="status"
                        label={k('list.columns.status')}
                        sort={sort}
                        onSort={setSort}
                      />
                      <th scope="col">{k('list.columns.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((item, index) => (
                      <tr key={item.id} data-selected={item.id === selected || undefined}>
                        <td>{(current - 1) * PAGE_SIZE + index + 1}</td>
                        <td className={classes.titleCell}>
                          {item.title}
                          <span className={classes.sub}>{item.raNo}</span>
                        </td>
                        <td className={classes.nowrap}>{item.vesselName}</td>
                        <td className={classes.nowrap}>
                          {t(`risk.departments.${item.department}`)}
                        </td>
                        <td>
                          {item.initial ? (
                            <LevelBadge level={item.initial.level} />
                          ) : (
                            <span className={classes.sub}>{k('list.unscored')}</span>
                          )}
                        </td>
                        <td data-numeric>{item.initial?.score ?? '—'}</td>
                        <td>
                          <RiskStatusBadge item={item} />
                        </td>
                        <td>
                          <span className={classes.actions}>
                            <Tooltip label={k('list.view', { no: item.raNo })}>
                              <ActionIcon
                                variant="subtle"
                                aria-label={k('list.view', { no: item.raNo })}
                                onClick={() => void open(item.id)}
                              >
                                <IconEye size={16} />
                              </ActionIcon>
                            </Tooltip>
                            {editor && item.can.edit ? (
                              <Tooltip label={k('list.edit', { no: item.raNo })}>
                                <ActionIcon
                                  variant="subtle"
                                  aria-label={k('list.edit', { no: item.raNo })}
                                  onClick={() => void open(item.id, 'details')}
                                >
                                  <IconEdit size={16} />
                                </ActionIcon>
                              </Tooltip>
                            ) : null}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
              <TablePager
                page={current}
                pages={pages}
                summary={k('list.showing', {
                  from: (current - 1) * PAGE_SIZE + 1,
                  to: Math.min(current * PAGE_SIZE, filtered.length),
                  total: filtered.length,
                })}
                label={k('list.pagination')}
                onPage={setPage}
              />
            </>
          )}
        </Panel>
      </div>

      <div className={classes.row3} ref={formRef}>
        <RiskForm
          detail={detail}
          draft={draft}
          editable={editable}
          dirty={dirty}
          busy={busy}
          tab={tab}
          notice={formNotice}
          banner={
            detail?.status === 'returned' && detail.returnNotice ? (
              <ReturnedBanner notice={detail.returnNotice} hint={editable} />
            ) : null
          }
          templateCode={templateCode}
          onTab={setTab}
          onChange={setDraft}
          onSave={() => void save()}
          onCancel={() => detail && setDraft(contentOf(detail))}
          actions={formActions}
          matrixLabel={matrixLabel}
          library={
            editable && draft && master ? (
              <LibraryStart
                master={master}
                matrix={matrix}
                existingHazards={draft.hazards.map((h) => h.hazard)}
                onAdd={(patch) => {
                  setDraft({
                    ...draft,
                    title: draft.title.trim() === '' ? patch.activity.name : draft.title,
                    activity:
                      draft.activity.trim() === ''
                        ? (patch.activity.scope ?? patch.activity.name)
                        : draft.activity,
                    hazards: [...draft.hazards, ...patch.hazards],
                    controls: [...draft.controls, ...patch.controls],
                  });
                  setNotice({
                    tone: 'info',
                    text: t('riskMaster.library.added', { count: patch.hazards.length }),
                  });
                }}
              />
            ) : undefined
          }
        />
        <Panel title={k('controls.title')}>
          {draft ? (
            <ControlTable controls={draft.controls} />
          ) : (
            <p className={classes.panelNote}>{k('controls.none')}</p>
          )}
          {editable && draft ? (
            <Button
              mt="sm"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                setDraft({
                  ...draft,
                  controls: [
                    ...draft.controls,
                    newControl(localId(), draft.hazards.at(-1)?.id ?? null),
                  ],
                });
                setTab('controls');
              }}
            >
              {k('controls.add')}
            </Button>
          ) : null}
        </Panel>
        <ScoringGuide />
      </div>

      <div className={classes.row4}>
        <Scenarios state={library} onUse={editor ? (entry) => void create(entry.id) : undefined} />
        <DecisionTree />
      </div>

      {detail ? (
        <>
          <ActionDialog
            opened={dialog === 'submit'}
            title={k('dialog.submitTitle')}
            body={k('dialog.submitBody', { no: nextRevision })}
            reason={nextRevision > 1 ? 'required' : 'none'}
            password
            confirmLabel={nextRevision > 1 ? k('form.submitRevision') : k('form.submit')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason, password }) =>
              act(() =>
                (editor as NonNullable<typeof editor>).submit(
                  detail.id,
                  nextRevision > 1 ? reason : null,
                  password || undefined,
                ),
              )
            }
          />
          <ActionDialog
            opened={dialog === 'approve'}
            title={k('dialog.approveTitle')}
            body={k('dialog.approveBody', { no: detail.revisionNo ?? 1 })}
            reason="optional"
            password
            confirmLabel={k('form.approve')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason, password }) =>
              expected
                ? act(() =>
                    source.decide(detail.id, {
                      decision: 'approved',
                      ...(reason ? { reason } : {}),
                      ...(password ? { password } : {}),
                      expected,
                    }),
                  )
                : Promise.resolve('NOT_PENDING')
            }
          />
          <ActionDialog
            opened={dialog === 'return'}
            title={k('dialog.returnTitle')}
            body={k('dialog.returnBody')}
            reason="required"
            password
            confirmLabel={k('form.return')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason, password }) =>
              expected
                ? act(() =>
                    source.decide(detail.id, {
                      decision: 'returned',
                      reason,
                      ...(password ? { password } : {}),
                      expected,
                    }),
                  )
                : Promise.resolve('NOT_PENDING')
            }
          />
          <ActionDialog
            opened={dialog === 'withdraw'}
            title={k('dialog.withdrawTitle')}
            body={k('dialog.withdrawBody')}
            reason="required"
            password
            danger
            confirmLabel={k('form.withdraw')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason, password }) =>
              act(() =>
                (editor as NonNullable<typeof editor>).withdraw(
                  detail.id,
                  reason,
                  password || undefined,
                ),
              )
            }
          />
          <ActionDialog
            opened={dialog === 'discard'}
            title={k('dialog.discardTitle')}
            body={k('dialog.discardBody')}
            reason="required"
            password={false}
            danger
            confirmLabel={k('form.discard')}
            onClose={() => setDialog(null)}
            onConfirm={({ reason }) =>
              act(async () => {
                await (editor as NonNullable<typeof editor>).discard(
                  detail.id,
                  detail.rowVersion,
                  reason,
                );
                return null;
              })
            }
          />
        </>
      ) : null}
      <Modal
        opened={starting}
        onClose={() => setStarting(false)}
        title={k('start.title')}
        closeButtonProps={{ 'aria-label': k('dialog.cancel') }}
      >
        <StartFromLibrary entries={currentEntries} onStart={(id) => void create(id)} />
      </Modal>
    </div>
  );
}

/**
 * Yeni gemi RA'sı (soru 8; ADR 0087 §5): yalnız bu gemiye atanmış güncel şirket sürümünden
 * başlar. Kalan risk ilk riske eşit gelir; gemi önlemleri doğrulayıp düşürür.
 */
function StartFromLibrary({
  entries,
  onStart,
}: {
  entries: readonly RiskLibraryEntry[];
  onStart: (versionId: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`risk.start.${key}`);
  const [picked, setPicked] = useState<string | null>(null);
  return (
    <form
      className={classes.dialogBody}
      onSubmit={(event) => {
        event.preventDefault();
        if (picked) onStart(picked);
      }}
    >
      <Select
        label={k('version')}
        description={k('help')}
        placeholder={entries.length > 0 ? k('placeholder') : k('none')}
        disabled={entries.length === 0}
        searchable
        value={picked}
        data={entries.map((e) => ({ value: e.id, label: `${e.code} ${e.revision} · ${e.title}` }))}
        onChange={setPicked}
      />
      <p className={classes.panelNote}>{k('residualNote')}</p>
      <div className={classes.formActions}>
        <Button type="submit" disabled={!picked}>
          {k('start')}
        </Button>
      </div>
    </form>
  );
}
